<template>
  <LazyHomeCommonLayout :is-blog-banner="true">
    <template #content>
      <section id="blog" class="blog subPageSection">
        <div class="backBg"></div>
        <div class="containerMain">
          <div class="detailPage blogDetail">
            <NuxtLink :to="{name: RouteName.HomeBlog}" class="backBtn flex"><span></span><p>{{ t("home-blog-all-post") }}</p></NuxtLink>
            <template v-if="isInit">
              <section class="blogBanner">
                <div class="imgArea"><img :src="blog.imageUrl" alt="thumbnail image"></div>
                <div class="infoArea flex">
                  <div class="label">{{ blog.label }}</div>
                  <h2>{{ blog.title }}</h2>
                  <div class="info">{{ blog.info }}</div>
                  <ul class="flex">
                    <li>{{ t("home-blog-author") }} <span> {{ blogData.author }}</span></li>
                    <li>{{ blogDate }}</li>
                  </ul>
                </div>
              </section>
              <section class="detailWrap flex">
                <div class="editorArea" style="width: 100%">
                  <!-- editor 영역 -->
                  <div v-html="content"></div>

                  <div class="bottomWrap">
                    <div class="tagArea flex">
                      <ul class="categoryArea">
                        <li v-if="blogData.tags?.length > 0">#</li>
                        <li v-for="(tag, index) in blogData.tags" :key="index">{{ tag }}</li>
                      </ul>
                      <div class="shareBtnWrap">
                        <button id="copyLink" class="share flex" @click.stop="methods.onClickShare"><span></span>{{ t("common-share") }}</button>
                      </div>
                    </div>
                    <div class="bottomArea flex">
                      <div class="imgArea">
                        <NuxtImg v-if="blogData.profile" :src="blogData.profile" format="webp" alt="profile image" />
                        <NuxtImg v-else src="~/assets/images/codepresso_profile_img.svg" alt="profile image" />
                      </div>
                      <div class="infoArea">
                        <div class="tit">{{ blogData.author }}</div>
                        <p>{{ blogData.authorDescription }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </template>
            <NuxtLink :to="{name: RouteName.HomeBlog}" class="backBtn flex"><span></span><p>{{ t("home-blog-all-post") }}</p></NuxtLink>
          </div>
        </div>
      </section>
      <LazyBlogAboutCodepresso />
      <div id="mainFooter"></div>
      <!-- pc 네비게이션 펼칠 때 on 클래스 -->
      <div class="navBg"></div>
    </template>
  </LazyHomeCommonLayout>
</template>

<script setup lang="ts">
import {useToastStore} from "~/store/toast/toastStore";
import {ToastType} from "~/components/Common/Toast/model/ToastType";
import {convertTimeToLocalFormatString, ref, useI18n, useSiteNavigationLd} from "#imports";
import {useHomeService} from "~/composables/service/useHomeService";
import {ArticleModel} from "~~/package/mapper/home/model/ArticleModel";
import {RouteName} from "~~/package/enum/RouteName";
import {BlogDataModel} from "~~/package/mapper/home/model/BlogDataModel";
import {useSeoMeta} from "~/composables/useSeoMeta";

const {$i18n} = useNuxtApp();
const { t } = useI18n();
const route = useRoute();
const toastStore = useToastStore();

const homeService = useHomeService();
const content = ref("");
const isInit = ref(false);

const blogId = computed(() => {
  return Number(route.params.blogId)
});

// Fetched at SSG build time so per-article OG tags land in the static HTML
// that SNS bots read. Default keeps the template safe before hydration.
const { data: blog, error } = await useAsyncData(
  `blog-${blogId.value}`,
  () => homeService.getArticle({id: blogId.value, link: undefined}),
  { default: () => new ArticleModel() },
);

if (error.value || isEmptyString(blog.value.title)) {
  await navigateTo('/404', { replace: true });
}

// 글마다 한 언어로만 작성되므로(예: 138번은 en, 137번은 ko), URL의 /en, /ko 같은 접두어가
// 실제 글 언어와 다르면 검색엔진/사용자에게 잘못된 언어 신호를 주게 된다. 접두어 없는
// 표준 경로로 301 리다이렉트해 그 글의 유일한 유효 접두어(자기 언어) + 접두어 없는 경로만
// 남긴다.
const routeLangPrefix = route.path.match(/^\/(en|ja|ko|es|vi)(?:\/|$)/);
if (routeLangPrefix && !isEmptyString(blog.value.lang) && routeLangPrefix[1] !== blog.value.lang.toLowerCase()) {
  await navigateTo(route.path.replace(/^\/(en|ja|ko|es|vi)/, '') || '/', { redirectCode: 301 });
}

const blogData = computed(() => {
  return blog.value.blogData ?? new BlogDataModel()
})
const blogDate = computed(() => {
  return convertTimeToLocalFormatString(blog.value.date, $i18n.locale.value);
})

const siteLinkScript = useSiteNavigationLd();
const { getCanonical, getHreflangLinks } = useSeoMeta();

useHead(() => {
  // BlogData carries optional meta overrides authored per-article. Fall back to
  // the top-level article fields when an override is not provided.
  const title = isEmptyString(blogData.value.metaTitle) ? blog.value.title : blogData.value.metaTitle;
  const description = isEmptyString(blogData.value.metaDescription) ? blog.value.info : blogData.value.metaDescription;
  const imageUrl = isEmptyString(blogData.value.metaImageUrl) ? blog.value.imageUrl : blogData.value.metaImageUrl;

  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: imageUrl },
      { property: 'og:url', content: `https://codepresso.io${route.path}` },
      { property: 'og:type', content: 'website' },
    ],
    script: [siteLinkScript],
    link: [...getHreflangLinks.value, { rel: 'canonical', href: getCanonical.value }],
  };
});

definePageMeta({
  alias: [
    '/en/home/blog/:blogId()',
    '/ja/home/blog/:blogId()',
    '/ko/home/blog/:blogId()',
    '/vi/home/blog/:blogId()',
  ],
})

onMounted(() => {
  methods.getContent().then(() => {
    isInit.value = true;
  });
})

const methods = {
  async getContent() {
    content.value = "";
    try {
      const response = await fetch(blogData.value.contentUrl);
      content.value = await response.text();
    } catch (error) {
      console.error('Error loading HTML:', error);
    }
  },
  onClickShare() {
    const dummy = document.createElement('input');
    const text = window.location.href;

    document.body.appendChild(dummy);
    dummy.value = text;
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);

    toastStore.openToast({
      toastType: ToastType.SUCCESS,
      contents: t("common-link-copy"),
    });
  }
}
</script>

<style>
@import "~/assets/scss/blog_style.scss";

#wrapper {
  background: #fafcff;
}
</style>